import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Pencil, Plus, Search, Trash2, Images } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, rupiah } from "../../lib/api";
import { usePolling } from "../../hooks/usePolling";
import { PageHeader } from "../../components/PageHeader";
import { ProductFormDialog } from "../../components/ProductFormDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";

const LinkCell = ({ url, testId }) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testId} className="block max-w-[140px] truncate text-xs text-blue-600 hover:underline">{url}</a>
  ) : <span className="text-xs text-slate-400">-</span>;

const Thumb = ({ photos }) => (
  <div className="relative h-12 w-12 overflow-hidden rounded-lg bg-slate-100">
    {photos?.[0] && <img src={fileUrl(photos[0])} alt="" className="h-full w-full object-cover" />}
    {photos?.length > 1 && (
      <span className="absolute bottom-0.5 right-0.5 flex items-center gap-0.5 rounded bg-slate-900/80 px-1 text-[9px] font-semibold text-white">
        <Images className="h-2.5 w-2.5" />{photos.length}
      </span>
    )}
  </div>
);

export default function Products() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const { data } = await api.get("/products");
    setItems(data);
  };
  usePolling(load, 6000);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setFormOpen(true); };

  const doDelete = async () => {
    try {
      await api.delete(`/products/${toDelete.product_id}`);
      toast.success("Produk dihapus");
      setItems((list) => list.filter((x) => x.product_id !== toDelete.product_id));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setToDelete(null);
    }
  };

  const filtered = (items || []).filter((p) => `${p.name} ${p.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Produk" subtitle="Kelola katalog produk affiliate kamu.">
        <Button onClick={openNew} data-testid="add-product-button" className="rounded-xl h-10"><Plus className="h-4 w-4 mr-1" />Tambah produk</Button>
      </PageHeader>
      <div className="rounded-2xl border border-slate-200 bg-white fade-up">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." className="pl-9" data-testid="product-search-input" />
          </div>
        </div>
        <div className="md:hidden divide-y divide-slate-100" data-testid="products-mobile-list">
          {items && !filtered.length && <p className="py-12 text-center text-sm text-slate-500">Belum ada produk.</p>}
          {filtered.map((p) => (
            <div key={p.product_id} className="p-4 flex gap-3" data-testid={`product-mobile-card-${p.product_id}`}>
              <Thumb photos={p.photos} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm line-clamp-2">{p.name}</p>
                <p className="text-xs text-slate-500">{p.category || "Tanpa kategori"} · {(p.clicks_shopee || 0) + (p.clicks_tiktok || 0) + (p.clicks_whatsapp || 0)} klik</p>
                <p className="mt-0.5 text-sm font-bold">{rupiah(p.price)} {p.original_price > 0 && <span className="text-xs font-normal text-slate-400 line-through">{rupiah(p.original_price)}</span>}</p>
                <div className="mt-1 flex flex-wrap gap-1 text-[10px] font-semibold">
                  {p.shopee_link && <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[#EE4D2D]">Shopee</span>}
                  {p.tiktok_link && <span className="rounded bg-slate-100 px-1.5 py-0.5">TikTok</span>}
                  {!p.shopee_link && !p.tiktok_link && <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">WhatsApp</span>}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/products/${p.product_id}`)} data-testid={`m-product-detail-button-${p.product_id}`}>Rincian</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`m-product-edit-button-${p.product_id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setToDelete(p)} className="text-red-600" data-testid={`m-product-delete-button-${p.product_id}`}>Hapus</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table data-testid="products-table">
            <TableHeader>
              <TableRow>
                <TableHead>Foto</TableHead><TableHead>Nama produk</TableHead><TableHead>Kategori</TableHead>
                <TableHead>Harga</TableHead><TableHead>Link Shopee</TableHead><TableHead>Link TikTok</TableHead>
                <TableHead className="text-right">Klik</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items && !filtered.length && (
                <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-slate-500" data-testid="products-empty">Belum ada produk. Klik "Tambah produk" untuk mulai.</TableCell></TableRow>
              )}
              {filtered.map((p) => (
                <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                  <TableCell><Thumb photos={p.photos} /></TableCell>
                  <TableCell className="font-semibold max-w-[200px]"><span className="line-clamp-2">{p.name}</span></TableCell>
                  <TableCell>{p.category ? <Badge variant="secondary">{p.category}</Badge> : "-"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="font-semibold">{rupiah(p.price)}</div>
                    {p.original_price > 0 && <div className="text-xs text-slate-400 line-through">{rupiah(p.original_price)}</div>}
                  </TableCell>
                  <TableCell><LinkCell url={p.shopee_link} testId={`product-shopee-link-${p.product_id}`} /></TableCell>
                  <TableCell>{p.shopee_link || p.tiktok_link ? <LinkCell url={p.tiktok_link} testId={`product-tiktok-link-${p.product_id}`} /> : <span className="text-xs font-semibold text-green-700">via WhatsApp</span>}</TableCell>
                  <TableCell className="text-right font-mono">{(p.clicks_shopee || 0) + (p.clicks_tiktok || 0) + (p.clicks_whatsapp || 0)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/products/${p.product_id}`)} data-testid={`product-detail-button-${p.product_id}`}><Eye className="h-3.5 w-3.5 mr-1" />Rincian</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`product-edit-button-${p.product_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setToDelete(p)} className="hover:bg-red-50 hover:text-red-600" data-testid={`product-delete-button-${p.product_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <ProductFormDialog open={formOpen} onOpenChange={setFormOpen} product={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" dan statistik kliknya akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-product-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-product-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
